import 'reflect-metadata'
import 'rxjs'
import 'zone.js'
import 'hammerjs'
import 'bootstrap'

import {
	HTTP_INTERCEPTORS,
	HttpErrorResponse,
	provideHttpClient,
	withInterceptorsFromDi,
} from '@angular/common/http'
import { appRoutingProviders, routing } from '@client/config/routing'
import {
	enableProdMode,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core'
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { CommonModule } from '@angular/common'
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser'
import { provideAnimations } from '@angular/platform-browser/animations'
import { AppComponent } from '@client/Components/App.Component'
import { PaginationService } from '@ui/Services/PaginationService'
import { AuthenticatedHttpService } from '@client/Services/AuthenticatedHttpService'
import { PermissionsService } from '@client/Services/PermissionsService'
import { SystemConfigService } from '@client/Services/SystemConfigService'
import HttpError from '@ui/HttpError'
import { RecaptchaService } from '@ui/Services/RecaptchaService'
import { WebAuthnService } from '@client/Services/WebAuthnService'
import { LoggedOutGuard } from '@client/Services/Guards/LoggedOutGuard'
import { LoggedInGuard } from '@client/Services/Guards/LoggedInGuard'
import { UsersService } from '@client/Services/UsersService'
import { PasskeysService } from '@client/Services/PasskeysService'
import * as Sentry from '@sentry/browser'
import AppErrorHandler from '@client/AppErrorHandler'
import { GuestService } from '@client/Services/GuestService'
import { OrdersService } from '@client/Services/OrdersService'
import { BillingService } from '@client/Services/BillingService'
import { provideToastr } from 'ngx-toastr'

const HttpListener = {
	provide: HTTP_INTERCEPTORS,
	useClass: AuthenticatedHttpService,
	multi: true,
}

const BoundErrorHandler = {
	provide: ErrorHandler,
	useClass: AppErrorHandler,
}

if (process.env.NODE_ENV === 'production') {
	enableProdMode()
}

bootstrapApplication(AppComponent, {
	providers: [
		provideZoneChangeDetection(),
		importProvidersFrom(
			routing,
			CommonModule,
			BrowserModule,
			FormsModule,
			ReactiveFormsModule,
		),
		provideHttpClient(withInterceptorsFromDi()),
		appRoutingProviders,
		HttpListener,
		BoundErrorHandler,
		provideAppInitializer(async () => {
			const systemConfigService = inject(SystemConfigService)
			const permissionsService = inject(PermissionsService)

			try {
				await systemConfigService.getConfig()
				await permissionsService.loadUser()
				if (process.env.NODE_ENV === 'production') {
					Sentry.init({
						dsn: systemConfigService.config.sentry.dsn,
						integrations: [Sentry.browserTracingIntegration()],
						release: systemConfigService.config.sentry.release,
					})
				}
			} catch (error) {
				if (
					!(error instanceof HttpError) &&
					!(error instanceof HttpErrorResponse)
				) {
					throw error
				}
			}
		}),
		FormBuilder,
		provideAnimations(),
		provideToastr(),
		PaginationService,
		PermissionsService,
		SystemConfigService,
		RecaptchaService,
		WebAuthnService,
		LoggedOutGuard,
		LoggedInGuard,
		UsersService,
		PasskeysService,
		GuestService,
		OrdersService,
		BillingService,
	],
}).catch((err) => {
	console.error(err)
})
